// Generic sortable-table helper. One pattern, used by every tab that renders
// a data table — AI breakdown, Top customers, Lost deals, Farming, Hygiene
// (Deal Explorer has its own slightly different sort because it sorts an
// `OpportunityRow` directly without per-column accessors).
//
// Usage from a view:
//
//   let sortState: SortState<MyRow> = { field: "won", dir: "desc" };
//
//   export function renderMyTab(host, rows) {
//     const { sorted, headHtml } = makeSortable(rows, COLUMNS, sortState);
//     host.innerHTML = `<table class="data-table sortable">${headHtml}<tbody>...</tbody></table>`;
//     bindSortHeaders(host, COLUMNS, sortState, (next) => {
//       sortState = next;
//       renderMyTab(host, rows);
//     });
//   }

import { esc } from "./format";

export type SortDir = "asc" | "desc";

export interface SortState<T> {
  field: keyof T;
  dir: SortDir;
}

export interface SortColumn<T> {
  /** key on the row whose value drives sorting (and identifies the column for clicks) */
  key: keyof T;
  /** column header text — escaped before injection */
  label: string;
  /** right-align numeric columns */
  numeric?: boolean;
  /** disable click-to-sort on this column (defaults to enabled) */
  sortable?: false;
  /**
   * Optional value extractor. Defaults to `row[key]`. Useful for derived
   * comparisons (e.g. sort the "Reason" column case-insensitively, or sort
   * a list-typed field by its length).
   */
  accessor?: (row: T) => string | number | null;
}

function getValue<T>(row: T, col: SortColumn<T>): string | number | null {
  if (col.accessor) return col.accessor(row);
  const v = row[col.key];
  if (v === null || v === undefined) return null;
  if (typeof v === "number" || typeof v === "string") return v;
  return String(v);
}

/**
 * Sort `rows` by `state`. NULLs always render last regardless of direction
 * (matches the backend's `nulls_last()` ordering on cursor pagination).
 *
 * Stability: `Array.prototype.sort` is stable per ECMAScript 2019, so equal
 * values preserve their input order without an explicit index.
 *
 * String comparison: uses `localeCompare` so accented characters and
 * mixed-case sort intuitively (e.g. "Acme" vs "acme" don't surprise).
 * Per PR-Agent correctness focus area on PR #16.
 */
export function sortRows<T>(rows: T[], cols: SortColumn<T>[], state: SortState<T>): T[] {
  const col = cols.find((c) => c.key === state.field);
  if (!col) return [...rows];
  const factor = state.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = getValue(a, col);
    const vb = getValue(b, col);
    if (va === vb) return 0;
    // NULLs always last — independent of direction.
    if (va === null) return 1;
    if (vb === null) return -1;
    if (typeof va === "string" && typeof vb === "string") {
      return va.localeCompare(vb) * factor;
    }
    if (va < vb) return -1 * factor;
    if (va > vb) return 1 * factor;
    return 0;
  });
}

/**
 * Build the `<thead><tr>...</tr></thead>` HTML for a sortable table. Each
 * sortable header carries `data-sort-key` (consumed by `bindSortHeaders`)
 * and the active column gets `.sorted-asc` / `.sorted-desc` (styled in
 * styles.css).
 */
export function renderSortableHead<T>(cols: SortColumn<T>[], state: SortState<T>): string {
  return `<thead><tr>${cols
    .map((c) => {
      const classes: string[] = [];
      if (c.numeric) classes.push("num");
      if (c.key === state.field) classes.push(`sorted-${state.dir}`);
      const attrs: string[] = [];
      if (c.sortable !== false) {
        // Keyboard accessibility (PR-Agent a11y note on PR #16): make the
        // header focusable + announce as a button. The keydown handler in
        // bindSortHeaders translates Enter/Space to a click.
        attrs.push(
          `data-sort-key="${esc(String(c.key))}"`,
          'role="button"',
          'tabindex="0"',
          `aria-label="Sort by ${esc(c.label)}"`,
        );
      }
      return `<th class="${classes.join(" ")}" ${attrs.join(" ")}>${esc(c.label)}</th>`;
    })
    .join("")}</tr></thead>`;
}

/**
 * Convenience wrapper: returns sorted rows AND the head HTML in one call.
 * Most callers want both.
 */
export function makeSortable<T>(
  rows: T[],
  cols: SortColumn<T>[],
  state: SortState<T>,
): { sorted: T[]; headHtml: string } {
  return {
    sorted: sortRows(rows, cols, state),
    headHtml: renderSortableHead(cols, state),
  };
}

/**
 * Bind click + keyboard handlers to every `<th data-sort-key="...">` inside
 * `host`. Cycle: clicking the active column flips direction; clicking a
 * different column sorts that column desc by default. `onChange` fires with
 * the new SortState — caller updates their own state holder + re-renders.
 *
 * Keyboard: Enter or Space on a focused header behaves like a click
 * (matches the `role="button"` advertised by `renderSortableHead`).
 *
 * Duplicate-binding safety: callers always replace `host.innerHTML` before
 * calling this, so the previous listeners are GC'd along with the old DOM
 * nodes. No tracking required.
 */
export function bindSortHeaders<T>(
  host: Element,
  cols: SortColumn<T>[],
  current: SortState<T>,
  onChange: (next: SortState<T>) => void,
): void {
  const trigger = (th: HTMLTableCellElement): void => {
    const key = th.dataset.sortKey;
    if (!key) return;
    const col = cols.find((c) => String(c.key) === key);
    if (!col) return;
    const sameField = col.key === current.field;
    const nextDir: SortDir = sameField && current.dir === "desc" ? "asc" : "desc";
    // SGNL-86 — keyboard focus preservation across re-render.
    // `onChange` typically triggers the consumer to replace `host`'s
    // entire innerHTML, which destroys the focused `<th>` and resets
    // focus to the document body. Capture the active sort-key BEFORE
    // the re-render, then locate the matching th in the freshly-
    // rendered DOM and restore focus.
    //
    // Synchronous restore: the onChange→re-render cycle in every
    // current consumer is fully synchronous (innerHTML replacement
    // happens before onChange returns), so a same-tick focus() lands
    // correctly. If a future consumer makes the re-render async
    // (e.g. RAF-batched), this would need to move to a microtask;
    // covered by `test_preserves_keyboard_focus...` regression test.
    onChange({ field: col.key, dir: nextDir });
    // Match by reading `dataset.sortKey` rather than interpolating
    // into a selector. Eliminates a class of selector-injection
    // breakage if `keyof T` ever produces a value with a quote or
    // bracket; also avoids `CSS.escape` (not reliably defined in
    // JSDOM where the tests run). PR #57 review CR-Q1 / Sec #3.
    const refocus = Array.from(
      host.querySelectorAll<HTMLTableCellElement>("th[data-sort-key]"),
    ).find((th) => th.dataset.sortKey === key);
    refocus?.focus();
  };
  for (const th of host.querySelectorAll<HTMLTableCellElement>("th[data-sort-key]")) {
    th.addEventListener("click", () => trigger(th));
    th.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        trigger(th);
      }
    });
  }
}
